import Button from "@components/common/Button";
import { RAQ_PAGE_TYPE } from "@components/utils/constants";
import { sanitizeTags } from "@components/utils/sanitizeHelpers";
import { sortPackagesAndLocations } from "@components/utils/sortingHelpers";
import {
  ILocationResponse,
  IPackageResponse,
} from "@customTypes/modelResponses";
import useAppContext from "@hooks/useAppContext";
import Card from "@libs/cards/Card";
import CardWrapper from "@libs/cards/CardWrapper";
import styledParser from "@libs/parser/styled-parser";
import SliderCarousel from "@libs/slider/SliderCarousel";
import useSlider from "@libs/slider/hooks/useSlider";
import Link from "next/link";
import { useMemo } from "react";

type RaqPageTypeKeys = keyof typeof RAQ_PAGE_TYPE;
type RaqPageType = (typeof RAQ_PAGE_TYPE)[RaqPageTypeKeys];

interface IIncludedPackagesProps {
  packages: IPackageResponse[] & ILocationResponse[];
  title: string;
  collectionType: RaqPageType;
  locationTitle: string;
  cardsToShow: number;
  pageType?: number;
  sectionText?: string;
  exploreAllUrl?: string;
}

function IncludedPackages(props: IIncludedPackagesProps): JSX.Element {
  const { isMobile } = useAppContext();

  const pageType = props.pageType;

  const cardComponents = useMemo(() => {
    const allPackages = props.packages || [];
    return sortPackagesAndLocations(allPackages).map(
      (pack: IPackageResponse & ILocationResponse) => {
        return (
          <CardWrapper key={pack?.id} widthPercentage={80}>
            <Card
              id={pack?.id}
              title={pack?.Title}
              blurb={pack?.Blurb}
              url={`${pack?.locations ? "/packages/" : ""}${pack?.SEO?.Slug}`}
              tags={sanitizeTags(pack?.tags)}
              image={
                (pack?.PrimaryImage?.length && pack?.PrimaryImage[0]) ??
                (pack?.Media?.PrimaryImage?.length &&
                  pack?.Media?.PrimaryImage[0])
              }
              imageSize={isMobile ? "medium" : "small"}
              hasLike={true}
              cardType={pack?.locations ? "package" : "location"}
            />
          </CardWrapper>
        );
      }
    );
  }, [props.packages, isMobile]);

  const sliderConfig = useMemo(() => {
    return {
      desktop: {
        slidesToShow: props?.cardsToShow,
      },
      mobile: {
        class: "mobile-carousel",
        variableWidth: true,
      },
    };
  }, [props.cardsToShow]);

  const { sliderSettings } = useSlider(sliderConfig);

  return (
    <div className={`my-8 ml-5 desktop:my-12 desktop:mx-0`}>
      <div className="flex items-start justify-between">
        <div className={`desktop:w-[623px]`}>
          <h2 className="mb-3 text-h2 font-semibold text-black desktop:mb-4">
            {pageType === 6
              ? props.title
              : `${props.title} Is Featured in These Trips`}
          </h2>
          <div className="w-[100%]">
            <div className="mb-[36px] text-p14 text-darkgray desktop:text-p">
              {pageType === 6
                ? props.sectionText && styledParser(props.sectionText)
                : props.collectionType === "accommodation"
                ? `If you want to stay at ${props.title} we recommend you to check out
              the following trips:`
                : `Here are some of the combo trips we recommend you to check out if you want to experience ${props.title}:`}
              {}
            </div>
          </div>
        </div>
      </div>
      {cardComponents.length > 0 && (
        <SliderCarousel
          key={`${props.title}_included_packages`}
          components={cardComponents}
          settings={sliderSettings}
        />
      )}

      {props.exploreAllUrl ? (
        <div className="mb-[3.125rem] mt-[2rem] flex w-full justify-center px-3 desktop:mb-[6rem] desktop:px-0">
          <div className="flex w-full max-w-[400px]">
            <Link href={props.exploreAllUrl} passHref>
              <Button variant="tertiary">Explore All</Button>
            </Link>
          </div>
        </div>
      ) : null}
    </div>
  );
}
export default IncludedPackages;
